import { K, mono, withheldLabel } from '../lib/theme'
import { fUsd, fBps, bpsGlyph, fAge, fDateET } from '../lib/format'
import type { Board as BoardData, Config, Sample } from '../lib/api'
import { WeekGrid } from './WeekGrid'
import { Signup } from './Signup'

const ANOMALY_BPS = 50

const bpsColor = (v: number | null | undefined) =>
  v == null ? K.dim : Math.round(v) > 0 ? K.up : Math.round(v) < 0 ? K.dn : K.txt

function Row({ s, onOpen }: { s: Sample; onOpen: () => void }) {
  const wh = s.withheld
  const sess = s.sessionState === 'open' ? 'open' : s.sessionState === 'holiday' ? 'holiday' : 'closed'
  return (
    <div className="ob-row" onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
      style={{ display: 'grid', gap: '10px 12px', padding: '12px 16px', borderBottom: '1px solid rgba(154,163,199,.09)', cursor: 'pointer', alignItems: 'start' }}>
      <div>
        <div style={{ fontFamily: mono, fontSize: 15, fontWeight: 500, color: K.txt }}>{s.sym}</div>
        <div style={{ fontSize: 12, color: K.dim, marginTop: 2 }}>{s.name}</div>
      </div>
      <div>
        <div className="ob-cell-label">On-chain</div>
        <div style={{ fontFamily: mono, fontSize: 14, color: K.txt }}>{fUsd(s.onchainUsd)}</div>
        <div style={{ fontSize: 11.5, color: K.dim, marginTop: 2, fontFamily: mono }}>{s.onchainEth ? s.onchainEth.toFixed(5) + ' Ξ' : '—'}</div>
      </div>
      <div>
        <div className="ob-cell-label">Reference</div>
        <div style={{ fontFamily: mono, fontSize: 14, color: s.refUsd ? K.txt : K.dim }}>{fUsd(s.refUsd)}</div>
        <div style={{ fontSize: 11.5, color: K.dim, marginTop: 2 }}>
          {s.refAt ? fDateET(s.refAt) + ' ET' : s.hasRef === false ? 'no public reference' : 'not read this sample'}
        </div>
      </div>
      <div>
        <div className="ob-cell-label">Ref age</div>
        <div style={{ fontFamily: mono, fontSize: 14, color: (s.refAgeMs ?? 0) > 24 * 3600_000 ? K.am : K.txt }}>{fAge(s.refAgeMs)}</div>
        <div style={{ fontSize: 11.5, color: K.dim, marginTop: 2 }}>{sess === 'open' ? 'live' : 'last print'}</div>
      </div>
      <div>
        <div className="ob-cell-label">Premium</div>
        {wh ? (
          <div style={{ fontFamily: mono, fontSize: 13, color: K.dim }}>{withheldLabel(wh)}</div>
        ) : (
          <>
            <div style={{ fontFamily: mono, fontSize: 14, color: bpsColor(s.premiumBps), display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <span style={{ fontSize: 10 }}>{bpsGlyph(s.premiumBps)}</span><span>{fBps(s.premiumBps)}</span>
            </div>
            <div style={{ fontSize: 11.5, color: K.dim, marginTop: 2 }}>vs last print</div>
          </>
        )}
      </div>
      <div>
        <div className="ob-cell-label">Session</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: mono, fontSize: 12, color: K.mut }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: sess === 'open' ? K.cy : K.am }} />
          {sess === 'open' ? 'open' : sess === 'holiday' ? 'holiday' : 'closed'}
        </div>
        <div style={{ fontSize: 11.5, color: K.dim, marginTop: 2, fontFamily: mono }}>{(s.fee / 10000).toFixed(2)}% pool</div>
      </div>
    </div>
  )
}

export function Board({ board, cfg, now, openTicker }: {
  board: BoardData | null; cfg: Config | null; now: Date; openTicker: (sym: string) => void
}) {
  const tokens = board?.tokens ?? []
  const printed = tokens.filter((t) => t.premiumBps != null)
  const anomalies = printed.filter((t) => Math.abs(t.premiumBps!) > ANOMALY_BPS)
  const week = board?.week ?? { openHours: 32.5, closedHours: 135.5, totalHours: 168, closedPct: 80.7 }

  const health = !board
    ? 'Reading the pools…'
    : anomalies.length
      ? `${anomalies.length} token${anomalies.length > 1 ? 's' : ''} beyond ±${ANOMALY_BPS} bps right now: ${anomalies.map((a) => `${a.sym} ${fBps(a.premiumBps)}`).join(', ')}.`
      : printed.length
        ? `All ${printed.length} printed premiums within ±${ANOMALY_BPS} bps. Nothing outside range.`
        : 'No premiums printable right now — every token is withheld. See the reasons in the table.'

  return (
    <main style={{ padding: '28px clamp(16px,3vw,28px) 0', maxWidth: 1240, margin: '0 auto' }}>
      <section className="ob-hero" style={{ display: 'grid', gap: '24px 48px', alignItems: 'end', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 'clamp(26px,3.2vw,38px)', lineHeight: 1.12, fontWeight: 500, margin: '0 0 14px', letterSpacing: '-.015em', textWrap: 'pretty' }}>
            The market is closed. These prices are not.
          </h1>
          <p style={{ margin: '0 0 10px', color: K.mut, fontSize: 15, lineHeight: 1.5, maxWidth: '58ch', textWrap: 'pretty' }}>
            The US equity market is open <span style={{ fontFamily: mono, color: K.txt }}>{week.openHours}</span> of{' '}
            <span style={{ fontFamily: mono, color: K.txt }}>{week.totalHours}</span> hours a week. Tokenized versions of these
            stocks trade on Uniswap pools all <span style={{ fontFamily: mono, color: K.txt }}>{week.totalHours}</span>. Offbell
            measures how far the on-chain price sits from the last real print, and for how long that print has been stale.
          </p>
          <p style={{ margin: 0, fontFamily: mono, fontSize: 12, color: K.dim, lineHeight: 1.5 }}>
            {week.closedHours} of {week.totalHours} h · {week.closedPct}% of the week · Robinhood Chain
          </p>
          <Signup cfg={cfg} />
        </div>
        <WeekGrid now={now} closedHours={week.closedHours} openHours={week.openHours} />
      </section>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0 14px', fontSize: 13, color: K.mut, lineHeight: 1.5 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: anomalies.length ? K.am : K.cy, flex: 'none', marginTop: 7 }} />
        <span>{health}</span>
      </div>

      <section style={{ border: `1px solid ${K.line}`, background: 'rgba(255,255,255,.015)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${K.line}` }}>
          <span style={{ fontSize: 13, color: K.mut }}>
            {tokens.length} stock tokens · Robinhood Chain · sampled every 5 min · tap a row for detail
          </span>
        </div>
        <div className="ob-row ob-head" style={{ display: 'grid', gap: 12, padding: '8px 16px', fontFamily: mono, fontSize: 10.5, color: K.dim, textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: `1px solid ${K.line}` }}>
          <span>Token</span><span>On-chain price</span><span>Reference price</span><span>Reference age</span><span>Premium</span><span>Session</span>
        </div>
        {tokens.map((s) => <Row key={s.sym} s={s} onOpen={() => openTicker(s.sym)} />)}
        {!tokens.length && <div style={{ padding: '28px 16px', color: K.dim, fontSize: 13 }}>Reading the pools…</div>}
        <div style={{ padding: '10px 16px', fontFamily: mono, fontSize: 11, color: K.dim, lineHeight: 1.5 }}>
          Premium = (on-chain − reference) / reference, in basis points, computed against the reference print shown and its age.
          Withheld when there is no public reference, the reference is stale beyond tolerance, or the pool is below the liquidity
          floor. Stock tokens are on-chain tokens tracking an equity; holding one is not ownership of the share.
        </div>
      </section>
    </main>
  )
}
